import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { describeAlsoAffected } from '@/lib/utils/soft-delete';
import { getDeletePreview, type SoftDeleteTarget } from '@/queries/soft-delete/soft-delete';

import { WorkPackageList } from './WorkPackageList';

type DeleteConfirmDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	target: SoftDeleteTarget;
	/** Shown in the title: "Delete Bridge A?" */
	name: string;
	isDeleting?: boolean;
	/** `acceptWorkPackageImpact` is true when the user confirmed deleting completed work packages with an asset. */
	onConfirm: (options: { acceptWorkPackageImpact: boolean }) => void | Promise<void>;
};

/**
 * Delete confirmation fed by the backend's delete preview (GH #331): what goes with the item, the
 * completed work packages deleted with an asset, and what blocks it (a damage or file using a
 * sub-asset, or a damage recorded in a work package). Delete is disabled while something blocks it;
 * the button reads "Delete anyway" when completed work packages go with the asset.
 */
export function DeleteConfirmDialog({
	open,
	onOpenChange,
	target,
	name,
	isDeleting = false,
	onConfirm,
}: DeleteConfirmDialogProps) {
	const { t } = useTranslation();
	const preview = useQuery({
		queryKey: ['soft-delete', 'delete-preview', target.path],
		queryFn: () => getDeletePreview(target),
		enabled: open,
		// Always fresh: what a delete takes along changes with every edit
		gcTime: 0,
		staleTime: 0,
		meta: { skipGlobalErrorToast: true },
	});
	const data = preview.data;
	const alsoDeleted = data ? describeAlsoAffected(data.willDelete, target.type) : '';
	const completedOwn = data?.workPackages.filter((workPackage) => workPackage.deletedWithAsset) ?? [];
	const blocked = (data?.blockedBy.length ?? 0) > 0;
	// Packages that block deleting a damage (or an inspection with it): linked so the user can remove the damage there
	const blockingIds = data?.blockedBy.find((blocker) => blocker.code === 'DAMAGE_IN_WORK_PACKAGE')?.workPackageIds;
	const blockingPackages = data?.workPackages.filter((workPackage) => blockingIds?.includes(workPackage.id)) ?? [];
	const requiresConfirmation = data?.requiresConfirmation ?? false;

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('common.deleted.deleteTitle', { name })}</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div className="space-y-3 text-sm">
							{preview.isLoading && (
								<span className="flex items-center gap-2">
									<Spinner className="size-4" /> {t('common.deleted.previewLoading')}
								</span>
							)}
							{preview.isError && <p>{t('common.deleted.previewFailed')}</p>}
							{alsoDeleted && <p>{t('common.deleted.deleteAlso', { items: alsoDeleted })}</p>}
							{data?.blockedBy.map((blocker) => (
								<p key={blocker.code} className="flex items-start gap-2 font-medium text-destructive">
									<TriangleAlert className="mt-0.5 size-4 shrink-0" />
									{t(`common.deleted.blocked.${blocker.code}`, {
										damages: blocker.damages ?? 0,
										dataUploads: blocker.dataUploads ?? 0,
									})}
								</p>
							))}
							{blockingPackages.length > 0 && <WorkPackageList workPackages={blockingPackages} showLines={false} />}
							{!blocked && completedOwn.length > 0 && (
								<div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
									<p className="flex items-start gap-2 font-medium">
										<TriangleAlert className="mt-0.5 size-4 shrink-0" />
										{t('common.deleted.completedWorkPackages')}
									</p>
									<WorkPackageList workPackages={completedOwn} showLines={false} />
								</div>
							)}
							{!blocked && <p className="text-muted-foreground">{t('common.deleted.deleteRestorable')}</p>}
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isDeleting}>{t('common.actions.cancel')}</AlertDialogCancel>
					<Button
						type="button"
						variant="destructive"
						disabled={isDeleting || preview.isLoading || blocked}
						onClick={() => void onConfirm({ acceptWorkPackageImpact: requiresConfirmation })}
					>
						{isDeleting && <Spinner className="size-4" />}
						{requiresConfirmation ? t('common.deleted.deleteAnyway') : t('common.actions.delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
